import React, { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import CrossDialogBtn from 'common/Button/CrossDialogBtn'
import NormalText from 'common/Typography/NormalText'
import { Formik } from 'formik'
import { FormikHelpers } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as yup from 'yup'

import { RefundDataCheck } from '../sharedComponents'

const validationSchema = yup.object({
  userId: yup.string().required('User ID is required'),
  ticketId: yup
    .string()
    .required('Ticket ID is required')
    .matches(/^[0-9]+$/, 'Must be only digits'),
  refundReason: yup.string().required('Refund reason is required')
})

export function RefundFormV2Dialog({
  transactionType,
  dialogOpen,
  handleDialogClose,
  paymentId,
  iccid,
  userId,
  email,
  refetchPaymentLogs,
  handleRefundRequestTabChange,
  isExternalUser
}: RefundFormProps) {
  const faberToken = OrbitSession.get('faberToken')
  const [isDataUsageZero, setIsDataUsageZero] = useState(false)
  const [isSubmitRefundLoading, setIsSubmitRefundLoading] = useState(false)

  const handleRefundLoading = () => {
    setIsSubmitRefundLoading(true)
  }

  const { mutate: refundUser } = useMutation(
    isExternalUser ? PaymentAPI.refundUserExt : PaymentAPI.refundUserV2,
    {
      onSuccess: () => {
        toast.success('Refund for customer received')
      },
      onError: () => {
        toast.error('There is an error processing refund for customer')
      },
      onSettled: () => {
        refetchPaymentLogs()
        handleDialogClose()
        handleRefundRequestTabChange && handleRefundRequestTabChange()
        setIsSubmitRefundLoading(false)
      }
    }
  )

  const { mutate: mutateRefundOtpack } = useMutation(
    isExternalUser
      ? PaymentAPI.refundUserExtAndTerminate
      : PaymentAPI.terminateOtpackUponRefund,
    {
      onSuccess: () => {
        toast.success('Refund for customer received')
      },
      onError: () => {
        toast.error('There is an error processing refund for customer')
      },
      onSettled: () => {
        refetchPaymentLogs()
        handleDialogClose()
        handleRefundRequestTabChange && handleRefundRequestTabChange()
        setIsSubmitRefundLoading(false)
      }
    }
  )

  const initialFormValues = useMemo(() => {
    return {
      userId: userId,
      refundPaymentID: paymentId,
      ticketId: '',
      refundReason: '',
      faberToken: faberToken,
      terminateSub: false
    }
  }, [isDataUsageZero, userId, paymentId])

  const handleFormSubmit = (
    values,
    actions: FormikHelpers<FormDetailsProps>
  ) => {
    refundUser(values, {
      onSettled: () => {
        actions.resetForm()
      }
    })
  }

  return (
    <Dialog
      open={dialogOpen}
      maxWidth='sm'
      fullWidth={true}
      onClose={handleDialogClose}
      aria-labelledby='draggable-dialog-title'
    >
      <Box sx={{ py: 2, px: 0 }}>
        <DialogTitle
          sx={{
            margin: '0',
            fontWeight: '700',
            color: 'black',
            fontSize: '21px',
            mt: '4px',
            px: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: '1em', mx: 2 }}>
              Refund Form
            </Typography>
            <CrossDialogBtn dialogClose={handleDialogClose} />
          </Box>
          <NormalText sx={{ px: 2, my: 0, color: 'primary.dark', mx: 0 }}>
            Payment ID: {paymentId}
          </NormalText>
        </DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          <Formik
            validationSchema={validationSchema}
            initialValues={initialFormValues}
            onSubmit={(values, actions) => handleFormSubmit(values, actions)}
          >
            <RefundDataCheck
              transactionType={transactionType}
              mutateRefundOtpack={mutateRefundOtpack}
              key='data-check'
              faberToken={faberToken}
              iccid={iccid}
              refundDialogOpen={dialogOpen}
              setIsDataUsageZero={setIsDataUsageZero}
              customerEmail={email}
              isRefundLoading={isSubmitRefundLoading}
              handleRefundLoading={handleRefundLoading}
              customerId={userId}
              paymentId={paymentId}
            />
          </Formik>
        </DialogContent>
      </Box>
    </Dialog>
  )
}

interface RefundFormProps {
  dialogOpen: boolean
  handleDialogClose: () => void
  paymentId: string
  refetchPaymentLogs: () => void
  iccid: string
  userId: string
  email: string
  handleRefundRequestTabChange?: () => void
  transactionType: string
  isExternalUser?: boolean
}

export interface FormDetailsProps {
  userId: string
  ticketId: string
  refundReason: string
  refundPaymentID: string
  faberToken: string
  terminateSub: boolean
}
